import { useShowContext } from 'framework/show';
import { useMessageService, ErrorMessage, SuccessMessage } from 'framework/ui/Message';
import useEmployeesDashboardService from '../useEmployeesDashboardService';
import { DashboardEmployee } from '../types';

export const useFollowUpManagement = () => {
  const { data, setData } = useShowContext<DashboardEmployee>();
  const { deleteFollowUp, updateFollowUp, createFollowUp } = useEmployeesDashboardService();
  const { addMessage } = useMessageService();

  const employeeData = data.getData();
  const { retentionRiskId: riskId } = employeeData;

  const handleFollowUpRemove = async (itemId: string | number, followUpId: string | number) => {
    try {
      const updatedFollowUps = employeeData.retentionRiskFollowUps.filter(f => f.id !== followUpId);
      setData!({
        ...employeeData,
        retentionRiskFollowUps: updatedFollowUps,
      });
      await deleteFollowUp(riskId, followUpId as string);
      addMessage(new SuccessMessage('Follow-up deleted successfully'));
    } catch (error) {
      setData!(employeeData);
      addMessage(new ErrorMessage('Failed to delete follow-up'));
    }
  };

  const handleFollowUpUpdate = async (
    itemId: string | number,
    followUpId: string | number,
    updateData: { description: string; date: string },
  ) => {
    try {
      const response = await updateFollowUp(riskId, followUpId as string, {
        description: updateData.description,
        followUpDate: updateData.date,
      });
      const updatedFollowUps = employeeData.retentionRiskFollowUps
        .filter(item => item.id !== followUpId)
        .concat(response)
        .sort((a, b) => new Date(a.followUpDate).getTime() - new Date(b.followUpDate).getTime());
      setData!({
        ...employeeData,
        retentionRiskFollowUps: updatedFollowUps,
      });
      addMessage(new SuccessMessage('Follow-up updated successfully'));
    } catch (error) {
      addMessage(new ErrorMessage('Failed to update follow-up'));
    }
  };

  const handleFollowUpAdd = async (itemId: string | number, addData: { description: string; date: string }) => {
    try {
      const response = await createFollowUp(riskId, {
        description: addData.description,
        followUpDate: addData.date,
      });
      const updatedFollowUps = [...employeeData.retentionRiskFollowUps, response].sort(
        (a, b) => new Date(a.followUpDate).getTime() - new Date(b.followUpDate).getTime(),
      );
      setData!({
        ...employeeData,
        retentionRiskFollowUps: updatedFollowUps,
      });
      addMessage(new SuccessMessage('Follow-up created successfully'));
    } catch (error) {
      addMessage(new ErrorMessage('Failed to create follow-up'));
    }
  };

  return {
    handleFollowUpAdd,
    handleFollowUpRemove,
    handleFollowUpUpdate,
  };
};
